class ABinstagramFeed extends HTMLElement {
  connectedCallback() {
    this.config = {
      container: this.querySelector(".swiper"),
      wrapper: this.querySelector(".swiper-wrapper"),
      spinner: this.querySelector(".loading-spinner"),
      token: this.dataset.accessToken?.trim(),
      limit: parseInt(this.dataset.postLimit) || 6,
      desktop: parseInt(this.dataset.desktopPerSlide) || 4,
      mobile: parseInt(this.dataset.mobilePerSlide) || 3,
      autoplay: this.dataset.autoplay === "true",
      navigation: this.dataset.showNavigation === "true",
      nextBtn: this.querySelector(".swiper-button-next"),
      prevBtn: this.querySelector(".swiper-button-prev"),
      acceptFields: "id,media_type,media_url,permalink,thumbnail_url",
    };

    this.loadedVideos = new Set();
    this.loadFeed();
  }

  initSwiper() {
    setTimeout(() => {
      try {
        const swiperConfig = {
          slidesPerView: this.config.mobile,
          spaceBetween: 5,
          grabCursor: true,
          watchOverflow: true,
          breakpoints: {
            750: { slidesPerView: this.config.desktop },
          },
        };

        if (this.config.navigation) {
          swiperConfig.navigation = {
            nextEl: this.config.nextBtn,
            prevEl: this.config.prevBtn,
          };
        }

        if (this.config.autoplay) {
          swiperConfig.autoplay = {
            delay: 3000,
            disableOnInteraction: false,
          };
        }

        this.swiper = new Swiper(this.config.container, swiperConfig);

        this.swiper.on('slideChange', () => this.handleSlideChange());
        this.swiper.on('reachBeginning', () => this.updateNavigation());
        this.swiper.on('reachEnd', () => this.updateNavigation());
        this.swiper.on('fromEdge', () => this.updateNavigation());

        // Initial arrow state — Swiper is fully ready here
        this.updateNavigation();

      } catch (error) {
        console.error('Swiper initialization error:', error);
      }
    }, 100);
  }

  async loadFeed() {
    if (!this.config.token) {
      this.showError("Instagram Access Token required");
      return;
    }

    try {
      const url = `https://graph.instagram.com/me/media?fields=${this.config.acceptFields}&limit=${this.config.limit}&access_token=${this.config.token}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const feeds = await response.json();

      if (feeds.error) throw new Error(feeds.error.message);
      if (!feeds.data?.length) throw new Error("No posts found");

      this.renderFeeds(feeds.data);
    } catch (error) {
      console.error("Instagram Feed Error:", error);
      this.showError(error.message || "Failed to load Instagram posts");
    }
  }

  renderFeeds(feeds) {
    const slides = feeds.map((post, index) => {
      const isVideo = post.media_type === "VIDEO";

      const media = isVideo
        ? `<div class="video-placeholder"
               data-video-url="${post.media_url}"
               data-permalink="${post.permalink}"
               data-index="${index}"
               role="button"
               aria-label="Play Instagram video">
            <img src="${post.thumbnail_url || ''}"
                 alt="Instagram video thumbnail"
                 class="video-thumbnail"
                 loading="lazy"
                 decoding="async">
            <div class="play-btn" aria-hidden="true">
              <svg viewBox="0 0 60 60" width="60" height="60" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="30" r="30" fill="rgba(0,0,0,0.45)"/>
                <polygon points="22,15 48,30 22,45" fill="white"/>
              </svg>
            </div>
          </div>`
        : `<a href="${post.permalink}" target="_blank" rel="noopener noreferrer">
            <img src="${post.media_url}"
                 alt="Instagram post"
                 loading="lazy"
                 decoding="async">
          </a>`;

      return `<div class="ab-insta-slide swiper-slide" data-index="${index}" data-media-type="${post.media_type}">
        ${media}
      </div>`;
    }).join("");

    this.config.wrapper.innerHTML = slides;
    this.config.spinner?.remove();
    this.initSwiper();
    this.bindVideoClicks();
    this.config.container.setAttribute("aria-hidden", "false");
  }

  bindVideoClicks() {
    this.querySelectorAll('.video-placeholder').forEach(placeholder => {
      placeholder.addEventListener('click', (e) => {
        const video = placeholder.querySelector('video');

        // If video is already playing — navigate to Instagram
        if (video) {
          const permalink = placeholder.dataset.permalink;
          if (permalink) window.open(permalink, '_blank', 'noopener,noreferrer');
          return;
        }

        // Otherwise load and play the video
        this.activateVideo(placeholder);
      });
    });
  }

  activateVideo(placeholder) {
    const index = placeholder.dataset.index;
    if (this.loadedVideos.has(index)) return;

    const videoUrl = placeholder.dataset.videoUrl;
    const permalink = placeholder.dataset.permalink;
    const playBtn = placeholder.querySelector('.play-btn');
    const thumbnail = placeholder.querySelector('.video-thumbnail');

    // Show spinner while loading
    if (playBtn) {
      playBtn.innerHTML = `<div class="video-loading-indicator"></div>`;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.autoplay = true;
    video.preload = 'none';
    video.className = 'instagram-video';
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const source = document.createElement('source');
    source.src = videoUrl;
    source.type = 'video/mp4';
    video.appendChild(source);

    video.addEventListener('loadeddata', () => {
      if (thumbnail) thumbnail.style.display = 'none';
      if (playBtn) playBtn.style.display = 'none';
      placeholder.appendChild(video);

      video.play().catch(() => {
        // Autoplay blocked — show thumbnail and play button again
        if (thumbnail) thumbnail.style.display = 'block';
        if (playBtn) {
          playBtn.style.display = 'flex';
          playBtn.innerHTML = `
            <svg viewBox="0 0 60 60" width="60" height="60" xmlns="http://www.w3.org/2000/svg">
              <circle cx="30" cy="30" r="30" fill="rgba(0,0,0,0.45)"/>
              <polygon points="22,15 48,30 22,45" fill="white"/>
            </svg>`;
        }
      });

      this.loadedVideos.add(index);
    }, { once: true });

    video.addEventListener('error', () => {
      console.error('Video load error for index:', index);
      if (playBtn) {
        playBtn.style.display = 'flex';
        playBtn.innerHTML = `
          <svg viewBox="0 0 60 60" width="60" height="60" xmlns="http://www.w3.org/2000/svg">
            <circle cx="30" cy="30" r="30" fill="rgba(0,0,0,0.45)"/>
            <polygon points="22,15 48,30 22,45" fill="white"/>
          </svg>`;
      }
    }, { once: true });

    video.load();
  }

  handleSlideChange() {
    if (!this.swiper) return;
    try {
      // Videos keep playing across slide changes — no pausing
      this.updateNavigation();
    } catch (error) {
      console.error('Slide change error:', error);
    }
  }

  updateNavigation() {
    if (!this.swiper) return;
    try {
      // Use Swiper's own flags — always accurate
      if (this.config.prevBtn) {
        this.config.prevBtn.classList.toggle("show", !this.swiper.isBeginning);
      }
      if (this.config.nextBtn) {
        this.config.nextBtn.classList.toggle("show", !this.swiper.isEnd);
      }
    } catch (error) {
      console.error('Navigation update error:', error);
    }
  }

  showError(message) {
    this.config.wrapper.innerHTML = `<div class="insta-error">⚠️ ${message}</div>`;
    this.config.spinner?.remove();
    this.config.container.setAttribute("aria-hidden", "false");
  }

  disconnectedCallback() {
    try {
      if (this.swiper?.destroy) this.swiper.destroy(true, true);
      this.loadedVideos.clear();
    } catch (error) {
      console.error('Cleanup error:', error);
    }
  }
}

customElements.define("ab-instagram-feed", ABinstagramFeed);